import React from 'react';
import camelCase from 'lodash/camelCase';
import cn from 'classnames';
import Floating, { Placement, IFloatingProps, IReferenceElementProps, IFloatingRef } from '@creditflowresearch/uilib/Floating';
import styles from './popover.module.scss';

export type PopoverPlacement = Placement;
export interface IPopoverRef extends IFloatingRef {}

export interface IProps {
  /**
   * The trigger element that opens the popover when interacted with
   */
  children:
        | React.ReactNode
        | ((props: { arrowAnchorRef: React.MutableRefObject<HTMLElement> }) => React.ReactNode),
  /**
   * Boolean indicating whether the popover is currently visible
   */
  isVisible?: boolean,
  /**
   * Function to set the visibility state of the popover
   */
  setIsVisible?: (open: boolean) => void,
  /**
   * Optional CSS class name for the wrapper element of the popover
   */
  wrapperClassName?: string,
  /**
   * Boolean indicating whether to render the popover in a portal (outside of its parent component)
   */
  usePortal?: boolean,
  /**
   * The content to be displayed inside the popover
   */
  content: React.ReactNode,
  /**
   * Boolean indicating whether the popover is disabled and cannot be interacted with
   */
  disabled?: boolean,
  /**
   * Boolean indicating whether the popover should flip its position when there is no space
   */
  isFlipEnabled?: boolean,
  /**
   * The preferred placement of the popover relative to its trigger element
   */
  placement?: PopoverPlacement,
  /**
   * Optional CSS class name for additional styling of the popover component
   */
  className?: string,
  /**
   * Optional CSS class name for styling the popover content
   */
  contentClassName?: string,
  /**
   * Optional CSS class name for styling the reference element
   */
  referenceClassName?: string,
  /**
   * Optional CSS class name for styling the popover arrow
   */
  arrowClassName?: string,
  /**
   * Minimum padding distance between the popover and the edges of its boundaries
   */
  minPadding?: number,
  /**
   * Boolean indicating whether the popover should use a dark theme for its appearance
   */
  isDarkTheme?: boolean,
  /**
   * Boolean indicating whether the popover's position is fixed in the viewport
   */
  positionFixed?: boolean,
  /**
   * Boolean indicating whether to enable preventing overflow of the popover content
   */
  preventOverflowEnabled?: boolean,
  /**
   * Boolean indicating whether to close the popover when clicking outside of it
   */
  closeOnClickOutside?: boolean,
  /**
   * Boolean indicating whether to close the popover when scrolling on an ancestor element
   */
  closeOnAncestorScroll?: boolean,
  /**
   * Callback function triggered after the popover is opened
   */
  afterOpen?: () => void,
  /**
   * Callback function triggered after the popover is closed
   */
  afterClose?: () => void,
  /**
   * Event that triggers the popover visibility ('click' or 'hover')
   */
  triggerEvent?: false | 'click' | 'hover',
  /**
   * Boolean indicating whether to render the Popover component only if it's visible
   */
  renderOnlyVisible?: boolean,
  /**
   * Unique identifier for testing purposes
   */
  dataTest?: string,
  /**
   * Options for offsetting the popover position
   * This can be used to adjust the distance between the popover and its trigger element.
   */
  offsetOptions?: IFloatingProps['positioning']['offsetOptions'],
}

const Popover = React.forwardRef(
  (
    {
      isVisible,
      setIsVisible,
      children,
      content,
      className,
      arrowClassName,
      contentClassName,
      wrapperClassName,
      referenceClassName,
      disabled = false,
      usePortal,
      isFlipEnabled,
      placement,
      minPadding,
      isDarkTheme = false,
      positionFixed,
      preventOverflowEnabled,
      closeOnClickOutside = true,
      closeOnAncestorScroll = true,
      triggerEvent = 'click',
      renderOnlyVisible = false,
      afterOpen,
      afterClose,
      dataTest,
      offsetOptions,
    }: IProps,
    ref: React.ForwardedRef<IPopoverRef>,
  ) => {
    return (
      <div className={ wrapperClassName }>
        <Floating
          ref={ ref }
          controls={ {
            open: isVisible,
            setOpen: setIsVisible,
            afterOpen,
            afterClose,
            disabled,
          } }
          positioning={ {
            /**
             * Note: If arrow support is added to Popover in the future,
             * see Tooltip's `computedOffsetOptions` for crossAxis offset logic
             * that keeps the arrow near the corner for start/end placements.
             */
            placement,
            usePortal,
            positionFixed,
            isFlipEnabled,
            preventOverflowEnabled,
            minPadding,
            offsetOptions,
          } }
          interactions={ {
            click: {
              enabled: triggerEvent === 'click',
              preventDefault: true,
            },
            hover: {
              enabled: triggerEvent === 'hover',
              handleClose: true,
            },
            dismiss: {
              onAncestorScroll: closeOnAncestorScroll,
              onOutsidePress: closeOnClickOutside,
            },
          } }
          reference={ ({ referenceProps, arrowAnchorRef }: IReferenceElementProps) => (
            <span className={ referenceClassName } { ...referenceProps }>
              { typeof children === 'function' ? children({ arrowAnchorRef }) : children }
            </span>
          ) }
          renderOnlyVisible={ renderOnlyVisible }
          floating={ ({ contentProps, arrowProps, placement, open }) => {
            return (
              <div
                className={ cn(styles.popover, className, {
                  [styles.isVisible]: open,
                  [styles.darkTheme]: isDarkTheme,
                }) }
                data-placement={ placement }
                data-test={ dataTest }
                { ...contentProps }
              >
                <div
                  className={ cn(
                    styles.arrowWrapper,
                    styles[camelCase(placement)],
                    arrowClassName,
                  ) }
                  { ...arrowProps }
                >
                  <div
                    className={ cn(
                      styles.arrow,
                      styles[camelCase(placement)],
                      arrowClassName,
                    ) }
                  />
                </div>
                <div
                  className={ cn(styles.content,
                    contentClassName,
                    styles[camelCase(placement)],
                  ) }
                >
                  { content }
                </div>
              </div>
            );
          } }
        />
      </div>
    );
  },
);

export default Popover;
